import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { CardTitleWithHelp, HoverHintContent, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import {
  MRP_LOCATIONS,
  compareMrpAssembliesAlphabetically,
  mrpAssemblyMatchesTlaPcbaFilters,
  type MrpAssembly,
  type MrpLocation,
} from "@/lib/mrp";

type SkuMap = Partial<Record<MrpLocation, string>>;

interface NewAssemblyState {
  code: string;
  display_name: string;
  notes: string;
}

const EMPTY_NEW_ASSEMBLY: NewAssemblyState = { code: "", display_name: "", notes: "" };

type AssemblySortKey = "code" | "display_name" | `sku_${MrpLocation}` | "actions";

function buildSkuMap(assembly: MrpAssembly): SkuMap {
  const map: SkuMap = {};
  for (const entry of assembly.skus) map[entry.location_code] = entry.sku;
  return map;
}

export function Assemblies() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftSkus, setDraftSkus] = useState<SkuMap>({});
  const [newAssembly, setNewAssembly] = useState<NewAssemblyState>(EMPTY_NEW_ASSEMBLY);
  const [showTlaAssemblies, setShowTlaAssemblies] = useState(true);
  const [showPcbaAssemblies, setShowPcbaAssemblies] = useState(true);
  const [sortKey, setSortKey] = useState<AssemblySortKey>("code");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const assembliesQuery = useQuery({
    queryKey: ["mrp", "assemblies"],
    queryFn: () => apiFetch<{ assemblies: MrpAssembly[] }>("/api/mrp/assemblies"),
  });

  const filteredAssemblies = useMemo(() => {
    const list = (assembliesQuery.data?.assemblies ?? []).filter((assembly) =>
      mrpAssemblyMatchesTlaPcbaFilters(assembly.code, showTlaAssemblies, showPcbaAssemblies),
    );
    const needle = search.trim().toLowerCase();
    const searched =
      needle.length === 0
        ? list
        : list.filter((assembly) => {
            if (assembly.code.toLowerCase().includes(needle)) return true;
            if (assembly.display_name.toLowerCase().includes(needle)) return true;
            return assembly.skus.some((entry) => entry.sku.toLowerCase().includes(needle));
          });
    if (sortKey === "code" || sortKey === "display_name") {
      searched.sort((left, right) => {
        const result = compareMrpAssembliesAlphabetically(left, right);
        return sortDir === "asc" ? result : -result;
      });
    } else if (sortKey.startsWith("sku_")) {
      const locationCode = sortKey.slice(4) as MrpLocation;
      searched.sort((left, right) => {
        const leftSku = buildSkuMap(left)[locationCode] ?? "";
        const rightSku = buildSkuMap(right)[locationCode] ?? "";
        const compared = leftSku.localeCompare(rightSku, undefined, { sensitivity: "base" });
        return sortDir === "asc" ? compared : -compared;
      });
    } else {
      searched.sort(compareMrpAssembliesAlphabetically);
    }
    return searched;
  }, [assembliesQuery.data, search, showPcbaAssemblies, showTlaAssemblies, sortDir, sortKey]);

  const filtersActive =
    search.trim() !== "" || !showTlaAssemblies || !showPcbaAssemblies;

  function toggleSort(key: AssemblySortKey) {
    if (key === "actions") return;
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  const createAssemblyMutation = useMutation({
    mutationFn: (input: NewAssemblyState) =>
      apiFetch("/api/mrp/assemblies", {
        method: "POST",
        body: { code: input.code, display_name: input.display_name, notes: input.notes || null },
      }),
    onSuccess: () => {
      setNewAssembly(EMPTY_NEW_ASSEMBLY);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "assemblies"] });
    },
  });

  const updateSkusMutation = useMutation({
    mutationFn: (input: { id: number; skus: { location_code: MrpLocation; sku: string }[] }) =>
      apiFetch(`/api/mrp/assemblies/${input.id}/skus`, {
        method: "PUT",
        body: { skus: input.skus },
      }),
    onSuccess: () => {
      setEditingId(null);
      setDraftSkus({});
      void queryClient.invalidateQueries({ queryKey: ["mrp", "assemblies"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    },
  });

  const startEdit = (assembly: MrpAssembly): void => {
    setEditingId(assembly.id);
    setDraftSkus(buildSkuMap(assembly));
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setDraftSkus({});
  };

  const saveEdit = (assembly: MrpAssembly): void => {
    const skus: { location_code: MrpLocation; sku: string }[] = [];
    for (const location of MRP_LOCATIONS) {
      const value = (draftSkus[location.code] ?? "").trim();
      if (value) skus.push({ location_code: location.code, sku: value });
    }
    updateSkusMutation.mutate({ id: assembly.id, skus });
  };

  const assemblyColumns = useMemo((): HubDataTableColumn<MrpAssembly, AssemblySortKey>[] => {
    const locationColumns: HubDataTableColumn<MrpAssembly, AssemblySortKey>[] = MRP_LOCATIONS.map(
      (location) => ({
        id: `sku_${location.code}`,
        header: `SKU @ ${location.label}`,
        sortKey: `sku_${location.code}` as AssemblySortKey,
        sortValue: (assembly) => buildSkuMap(assembly)[location.code] ?? "",
        cell: (assembly) => {
          const editing = editingId === assembly.id;
          const skuMap = editing ? draftSkus : buildSkuMap(assembly);
          if (editing) {
            return (
              <AssemblySkuCombobox
                value={skuMap[location.code] ?? ""}
                disabled={!hubWritesAllowed}
                onChange={(next) =>
                  setDraftSkus((current) => ({ ...current, [location.code]: next }))
                }
                placeholder={`SKU @ ${location.label}`}
              />
            );
          }
          if (skuMap[location.code]) {
            return (
              <span className="inline-flex items-center gap-2">
                <span className="font-mono text-xs">{skuMap[location.code]}</span>
                <HoverHintContent hoverHint={`Explode BoM for ${skuMap[location.code]}`}>
                  <Link
                    to={`/bom?sku=${encodeURIComponent(skuMap[location.code] ?? "")}`}
                    className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 hover:border-brand-500 hover:text-brand-700"
                  >
                    BoM
                  </Link>
                </HoverHintContent>
              </span>
            );
          }
          return <span className="text-xs italic text-slate-400">none</span>;
        },
      }),
    );

    return [
      {
        id: "code",
        header: "Assembly",
        sortKey: "code",
        sortValue: (assembly) => assembly.code,
        cell: (assembly) => (
          <>
            <div className="font-mono text-xs text-slate-900">{assembly.code}</div>
            {assembly.display_name && assembly.display_name !== assembly.code ? (
              <div className="text-xs text-slate-500">{assembly.display_name}</div>
            ) : null}
            {assembly.notes ? (
              <div className="mt-1 text-xs text-slate-500">{assembly.notes}</div>
            ) : null}
          </>
        ),
      },
      ...locationColumns,
      {
        id: "actions",
        header: "Actions",
        sortKey: "actions",
        align: "right",
        sortValue: () => "",
        cell: (assembly) => {
          const editing = editingId === assembly.id;
          if (editing) {
            return (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={cancelEdit}>
                  Cancel
                </Button>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
                  <Button
                    size="sm"
                    onClick={() => saveEdit(assembly)}
                    isLoading={updateSkusMutation.isPending}
                    disabled={!hubWritesAllowed}
                  >
                    Save
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
            );
          }
          return (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
              <Button
                size="sm"
                variant="secondary"
                disabled={!hubWritesAllowed}
                onClick={() => startEdit(assembly)}
              >
                Edit SKUs
              </Button>
            </ReadOnlyHoverLayer>
          );
        },
      },
    ];
  }, [
    cancelEdit,
    draftSkus,
    editingId,
    hubWritesAllowed,
    mrpReadOnlyTitle,
    saveEdit,
    startEdit,
    updateSkusMutation.isPending,
  ]);

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Logical assemblies map to physical SKUs per location. The forecast engine reads these mappings to explode the right BoM at each site.">
          Assemblies
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="After creating, click Edit on the new row to assign physical SKUs per location.">
            New assembly
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
            <form
              className="grid gap-3 md:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!hubWritesAllowed) return;
                if (!newAssembly.code.trim() || !newAssembly.display_name.trim()) return;
                createAssemblyMutation.mutate(newAssembly);
              }}
            >
              <div>
                <Label>Code</Label>
                <Input
                  value={newAssembly.code}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setNewAssembly((prev) => ({ ...prev, code: event.target.value }))}
                  placeholder="FI-3C-N-TLA-A"
                />
              </div>
              <div>
                <Label>Display name</Label>
                <Input
                  value={newAssembly.display_name}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setNewAssembly((prev) => ({ ...prev, display_name: event.target.value }))}
                  placeholder="FI-3C TLA-A (N)"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={newAssembly.notes}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setNewAssembly((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="optional"
                />
              </div>
              <div className="md:col-span-3 flex items-center justify-end gap-3">
                <Button
                  type="submit"
                  size="sm"
                  isLoading={createAssemblyMutation.isPending}
                  disabled={
                    !hubWritesAllowed ||
                    !newAssembly.code.trim() ||
                    !newAssembly.display_name.trim()
                  }
                >
                  Create assembly
                </Button>
              </div>
              {createAssemblyMutation.error ? (
                <p className="md:col-span-3 text-sm text-rose-700">
                  {(createAssemblyMutation.error as Error).message}
                </p>
              ) : null}
            </form>
          </ReadOnlyHoverLayer>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <div className="min-w-[12rem] flex-1">
            <Label htmlFor="assembly-search">Search</Label>
            <Input
              id="assembly-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by code, name, or SKU…"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Assembly code type
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={showTlaAssemblies ? "primary" : "secondary"}
                onClick={() => setShowTlaAssemblies((current) => !current)}
              >
                TLA
              </Button>
              <Button
                size="sm"
                variant={showPcbaAssemblies ? "primary" : "secondary"}
                onClick={() => setShowPcbaAssemblies((current) => !current)}
              >
                PCBA
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={`${filteredAssemblies.length} assemblies`}
        columns={assemblyColumns}
        rows={filteredAssemblies}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={assembliesQuery.isLoading}
        error={assembliesQuery.error != null ? (assembliesQuery.error as Error).message : null}
        empty="No assemblies."
        isFiltered={filtersActive}
        filteredEmpty="No assemblies match this filter."
        tableClassName="min-w-full text-sm"
      />
      {updateSkusMutation.error ? (
        <p className="text-sm text-rose-700">{(updateSkusMutation.error as Error).message}</p>
      ) : null}
    </div>
  );
}
